import React, { useEffect, useState } from "react";
import useFocusTrap from "../../../hooks/useFocusTrap";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Field from "../../../components/v3/Field";
import Btn from "../../../components/v3/Btn";
import Pill from "../../../components/v3/Pill";
import { formatAuditTimestamp } from "../../../utils/fmtDate";
import { bdcApi } from "../../../services/bdcApi";

const MIN_REASON = 10;
const VALID_SOURCES = ["LINK", "CODE", "MANUAL", "ADMIN_OVERRIDE"];

const backdropStyle = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
  zIndex: 1000, display: "grid", placeItems: "center", padding: 20,
};
const dialogStyle = {
  background: "var(--bg-card)", border: "1px solid var(--bd)",
  borderRadius: "var(--r-md)", padding: 20, maxWidth: 560, width: "100%",
  maxHeight: "90vh", overflow: "auto",
  boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
};

function AttributionEditor() {
  const [ledgerId, setLedgerId] = useState("");
  const [attributedBdcId, setAttributedBdcId] = useState("");
  const [subContractorBdcId, setSubContractorBdcId] = useState("");
  const [source, setSource] = useState("ADMIN_OVERRIDE");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(null);
  const confirmRef = useFocusTrap({
    active: !!pending,
    onClose: () => !submitting && setPending(null),
  });

  const [searchEmail, setSearchEmail] = useState("");
  const [searchEntityType, setSearchEntityType] = useState("");
  const [searchBdcId, setSearchBdcId] = useState("");
  const [searchSource, setSearchSource] = useState("");
  const [searchLocked, setSearchLocked] = useState("");
  const [searchRows, setSearchRows] = useState([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searchDirty, setSearchDirty] = useState(false);

  async function runSearch(ev) {
    if (ev && typeof ev.preventDefault === "function") ev.preventDefault();
    setSearchLoading(true);
    setSearchError(null);
    setSearchDirty(true);
    const params = { limit: 50 };
    if (searchEmail.trim()) params.investor_email = searchEmail.trim();
    if (searchEntityType) params.entity_type = searchEntityType;
    if (searchBdcId.trim() && Number(searchBdcId) > 0) params.attributed_bdc_id = Number(searchBdcId);
    if (searchSource) params.source = searchSource;
    if (searchLocked) params.locked = searchLocked;
    try {
      const res = await bdcApi.adListAttributions(params);
      setSearchRows(res?.data?.rows || []);
      setSearchTotal(res?.data?.total || 0);
    } catch (err) {
      setSearchError(err?.response?.data?.message || err.message || "Search failed");
      setSearchRows([]);
      setSearchTotal(0);
    } finally {
      setSearchLoading(false);
    }
  }

  useEffect(() => { runSearch(); /* eslint-disable-next-line */ }, []);

  function prefillFromRow(row) {
    setLedgerId(String(row.id));
    setAttributedBdcId(String(row.attributed_bdc_id || ""));
    setSubContractorBdcId(row.sub_contractor_bdc_id ? String(row.sub_contractor_bdc_id) : "");
    setSource(row.source || "ADMIN_OVERRIDE");
    setReason("");
    setResult(null);
    setError(null);
    try {
      document.getElementById("ad-attr-ledger-id")?.focus({ preventScroll: false });
    } catch (_) { /* best effort */ }
  }

  const reasonOk = reason.trim().length >= MIN_REASON;
  const canReview =
    Number(ledgerId) > 0 &&
    Number(attributedBdcId) > 0 &&
    VALID_SOURCES.includes(source) &&
    reasonOk &&
    !submitting;

  function handleReview(ev) {
    ev.preventDefault();
    if (!canReview) return;
    setError(null);
    setResult(null);
    const body = {
      attributed_bdc_id: Number(attributedBdcId),
      source,
      reason: reason.trim(),
    };
    if (subContractorBdcId.trim()) {
      body.sub_contractor_bdc_id = Number(subContractorBdcId);
    }
    setPending({ ledgerId: Number(ledgerId), body });
  }

  async function commit() {
    if (!pending || submitting) return;
    setSubmitting(true);
    try {
      const res = await bdcApi.adOverrideAttribution(pending.ledgerId, pending.body);
      setResult(res?.data || null);
      setReason("");
      setPending(null);
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || "Override failed";
      const details = err?.response?.data?.data || null;
      if (msg === "self_attribution_rejected") {
        const entityId = details?.entityId ?? details?.entity_id;
        const bdcId = details?.attributedBdcId ?? details?.attributed_bdc_id;
        const detailTail = (entityId != null && bdcId != null)
          ? ` (entity ${entityId} === BDC ${bdcId})`
          : "";
        setError("Self-attribution rejected — a BDC cannot be attributed to themselves" + detailTail);
      } else {
        setError(msg);
      }
      setPending(null);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PageShell>
      <PageHead title="Attribution override" />

      <p style={{ color: "var(--tx-3)", fontSize: "var(--t-sm)", margin: "0 0 12px" }}>
        Manually change the BDC attributed to an existing <code>attribution_ledger</code> row. Every override writes an <code>attribution_audit</code> entry with the reason and your user id. Self-attribution (entity === new BDC) is rejected with 422.
      </p>

      <section className="card">
        <div className="card__head"><div className="card__title">Lookup</div></div>
        <div className="card__body">
          <form onSubmit={runSearch}>
            <div className="form-grid form-grid--3col">
              <Field
                label="Investor email contains"
                name="search_email"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                placeholder="qa-inv-"
              />
              <Field
                as="select"
                label="Entity type"
                name="search_entity_type"
                value={searchEntityType}
                onChange={(e) => setSearchEntityType(e.target.value)}
              >
                <option value="">Any</option>
                <option value="INVESTOR">Investor</option>
                <option value="DEAL">Deal</option>
                <option value="CONSULTANT">Consultant</option>
              </Field>
              <Field
                label="Attributed BDC id"
                name="search_bdc_id"
                type="number"
                min={1}
                value={searchBdcId}
                onChange={(e) => setSearchBdcId(e.target.value)}
              />
              <Field
                as="select"
                label="Source"
                name="search_source"
                value={searchSource}
                onChange={(e) => setSearchSource(e.target.value)}
              >
                <option value="">Any</option>
                {VALID_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                <option value="LEGACY">LEGACY</option>
              </Field>
              <Field
                as="select"
                label="Lock state"
                name="search_locked"
                value={searchLocked}
                onChange={(e) => setSearchLocked(e.target.value)}
              >
                <option value="">Any</option>
                <option value="true">Locked</option>
                <option value="false">Unlocked</option>
              </Field>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <Btn variant="primary" type="submit" disabled={searchLoading}>
                {searchLoading ? "Searching…" : "Search"}
              </Btn>
              {searchDirty && (
                <Btn
                  variant="ghost"
                  type="button"
                  onClick={() => {
                    setSearchEmail(""); setSearchEntityType(""); setSearchBdcId("");
                    setSearchSource(""); setSearchLocked(""); setSearchDirty(false);
                    runSearch();
                  }}
                >
                  Reset
                </Btn>
              )}
            </div>
          </form>

          {searchError && <div style={{ marginTop: 12 }}><Alert tone="danger">{searchError}</Alert></div>}

          <div style={{ marginTop: 12, color: "var(--tx-3)", fontSize: "var(--t-sm)", fontFamily: "var(--f-mono)" }}>
            {searchTotal} match{searchTotal === 1 ? "" : "es"} (showing up to 50)
          </div>
        </div>
        <div className="card__body card__body--flush">
          {searchRows.length === 0 ? (
            <Empty>No attribution rows match the filters above.</Empty>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Entity</th>
                  <th>Attributed BDC</th>
                  <th>Sub</th>
                  <th>Source</th>
                  <th>Locked</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {searchRows.map((row) => {
                  const entityLabel =
                    row.Entity
                      ? row.Entity.email
                      : row.entity_type === "DEAL"
                      ? `Deal #${row.entity_id}`
                      : `#${row.entity_id}`;
                  const attributedBdcLabel =
                    row.AttributedBdc
                      ? row.AttributedBdc.email
                      : `#${row.attributed_bdc_id}`;
                  return (
                    <tr key={row.id}>
                      <td className="mono">#{row.id}</td>
                      <td>
                        <div>{entityLabel}</div>
                        <div className="tbl-id">{row.entity_type}</div>
                      </td>
                      <td className="mono">{attributedBdcLabel}</td>
                      <td className="muted">
                        {row.sub_contractor_bdc_id ? `#${row.sub_contractor_bdc_id}` : "—"}
                      </td>
                      <td><Pill tone="blue">{row.source}</Pill></td>
                      <td className="muted">{row.locked_at ? formatAuditTimestamp(row.locked_at) : "—"}</td>
                      <td className="r">
                        <Btn variant="ghost" size="sm" onClick={() => prefillFromRow(row)}>Use →</Btn>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card__head"><div className="card__title">Override</div></div>
        <div className="card__body">
          <form onSubmit={handleReview}>
            <div className="form-grid form-grid--2col">
              <Field
                label="Ledger row id"
                name="ad-attr-ledger-id"
                id="ad-attr-ledger-id"
                type="number"
                min={1}
                value={ledgerId}
                onChange={(e) => setLedgerId(e.target.value)}
                required
                disabled={submitting || !!pending}
              />
              <Field
                label="New attributed BDC user id"
                name="ad-attr-bdc-id"
                type="number"
                min={1}
                value={attributedBdcId}
                onChange={(e) => setAttributedBdcId(e.target.value)}
                required
                disabled={submitting || !!pending}
              />
              <Field
                label="Sub-contractor BDC user id (optional)"
                name="ad-attr-sub-id"
                type="number"
                min={1}
                value={subContractorBdcId}
                onChange={(e) => setSubContractorBdcId(e.target.value)}
                disabled={submitting || !!pending}
              />
              <Field
                as="select"
                label="Source"
                name="ad-attr-source"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                disabled={submitting || !!pending}
              >
                {VALID_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </Field>
            </div>

            <div className="field" style={{ marginTop: 12 }}>
              <label className="field__label" htmlFor="ad-attr-reason">
                Reason (min {MIN_REASON} chars) — audited
              </label>
              <textarea
                id="ad-attr-reason"
                className="field__textarea"
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                minLength={MIN_REASON}
                disabled={submitting || !!pending}
              />
              <span className="field__hint">{reason.trim().length} / {MIN_REASON}</span>
            </div>

            <div style={{ marginTop: 12 }}>
              <Btn variant="primary" type="submit" disabled={!canReview}>Review override</Btn>
            </div>

            {error && <div style={{ marginTop: 12 }}><Alert tone="danger">{error}</Alert></div>}
            {result && (
              <div style={{ marginTop: 12 }}>
                <Alert tone="info">
                  Override applied {formatAuditTimestamp(new Date().toISOString())}. Ledger #{result.id} → BDC #{result.attributed_bdc_id} (source {result.source}). Audit row #{result.attribution_audit_id}.
                </Alert>
              </div>
            )}
          </form>
        </div>
      </section>

      {pending && (
        <div
          style={backdropStyle}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ad-attr-confirm-title"
          ref={confirmRef}
        >
          <div style={dialogStyle}>
            <h3 id="ad-attr-confirm-title" style={{ fontSize: "var(--t-xl)", fontWeight: 600, margin: "0 0 12px" }}>
              Confirm attribution override
            </h3>
            <dl style={{ margin: "0 0 16px" }}>
              <KV label="Ledger row" value={`#${pending.ledgerId}`} />
              <KV label="New attributed BDC" value={`#${pending.body.attributed_bdc_id}`} />
              <KV label="Sub-contractor BDC" value={pending.body.sub_contractor_bdc_id ? `#${pending.body.sub_contractor_bdc_id}` : "—"} />
              <KV label="Source" value={pending.body.source} />
              <KV label="Reason" value={pending.body.reason} />
            </dl>
            <Alert tone="warn">
              This writes an immutable <code>attribution_audit</code> row with your user id. Press <strong>Cancel</strong> to go back — or <strong>Apply override</strong> to commit.
            </Alert>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <Btn type="button" onClick={() => setPending(null)} disabled={submitting}>Cancel</Btn>
              <Btn variant="danger" type="button" onClick={commit} disabled={submitting} autoFocus>
                {submitting ? "Applying…" : "Apply override"}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}

const KV = ({ label, value }) => (
  <>
    <dt className="field__label" style={{ marginTop: 8 }}>{label}</dt>
    <dd style={{ margin: "2px 0 0", fontSize: "var(--t-md)" }}>{value}</dd>
  </>
);

export default AttributionEditor;
